// Reporting a person or a group (docs/design/social-plan.md 4.9): what is wrong with it (the whole,
// its name or its picture), why, and an optional note only the operator reads. Afterwards a
// person can also block the reported one, which is the reporter's own choice and asks first.
import { REPORT_REASONS, type ReportReason, type ReportSubjectType } from "@proef/contract";
import { useState } from "react";
import { useConfirm } from "./ConfirmSheet";
import { EdgeApiError } from "../lib/edgeApi";
import { mk, useT, type MessageKey } from "../lib/i18n";
import { blockUser } from "../lib/social/friendsApi";
import { reportSubject } from "../lib/social/peopleApi";
import {
  Banner,
  Body,
  Button,
  Column,
  ErrorBanner,
  Heading,
  SelectField,
  Sheet,
  TextArea,
} from "../ui";

export interface ReportedSubject {
  kind: "user" | "group";
  id: string;
  name: string;
}

export interface ReportSheetProps {
  subject: ReportedSubject;
  onClose: () => void;
}

type Aspect = "whole" | "name" | "avatar";
const ASPECTS: readonly Aspect[] = ["whole", "name", "avatar"];

function subjectTypeOf(kind: ReportedSubject["kind"], aspect: Aspect): ReportSubjectType {
  if (aspect === "whole") return kind;
  return `${kind}_${aspect}`;
}

function isAspect(value: string): value is Aspect {
  return (ASPECTS as readonly string[]).includes(value);
}

function isReason(value: string): value is ReportReason {
  return (REPORT_REASONS as readonly string[]).includes(value);
}

export function ReportSheet({ subject, onClose }: ReportSheetProps): React.JSX.Element {
  const { t } = useT();
  const { ask, confirmation } = useConfirm();
  const [aspect, setAspect] = useState<Aspect>("whole");
  const [reason, setReason] = useState<ReportReason>("spam");
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [blocked, setBlocked] = useState(false);

  const title = t("people.report.title", { name: subject.name });

  const send = (): void => {
    if (sending) return;
    setSending(true);
    setError(undefined);
    const trimmed = note.trim();
    reportSubject({
      subjectType: subjectTypeOf(subject.kind, aspect),
      subjectId: subject.id,
      reason,
      ...(trimmed === "" ? {} : { note: trimmed }),
    })
      .then(() => {
        setSent(true);
        return undefined;
      })
      .catch((err: unknown) => {
        setError(
          err instanceof EdgeApiError && err.status === 429
            ? t("people.report.limit")
            : t("common.error.generic"),
        );
      })
      .finally(() => {
        setSending(false);
      });
  };

  const wholeKey: MessageKey =
    subject.kind === "user"
      ? "people.report.aspect.whole.user"
      : "people.report.aspect.whole.group";

  return (
    <Sheet label={title} onClose={onClose} testId="report-sheet">
      {sent ? (
        <Column gap="3">
          <Heading render="h2">{t("people.report.sent.title")}</Heading>
          <Body>{t("people.report.sent.body")}</Body>
          {subject.kind === "user" && !blocked && (
            <Button
              full
              tone="secondary"
              label={t("people.report.sent.block", { name: subject.name })}
              onPress={() => {
                ask({
                  title: t("people.confirm.block.title", { name: subject.name }),
                  body: t("people.confirm.block.body", { name: subject.name }),
                  confirmLabel: t("people.confirm.block.yes"),
                  onConfirm: async () => {
                    await blockUser(subject.id);
                    setBlocked(true);
                  },
                });
              }}
            />
          )}
          <Button full label={t("people.report.sent.done")} onPress={onClose} />
          {confirmation}
        </Column>
      ) : (
        <Column gap="3">
          <Heading render="h2">{title}</Heading>
          <SelectField
            label={t("people.report.aspect")}
            value={aspect}
            options={[
              { value: "whole", label: t(wholeKey) },
              { value: "name", label: t("people.report.aspect.name") },
              { value: "avatar", label: t("people.report.aspect.avatar") },
            ]}
            onValueChange={(value) => {
              if (isAspect(value)) setAspect(value);
            }}
          />
          <SelectField
            label={t("people.report.reason")}
            value={reason}
            options={REPORT_REASONS.map((value) => ({
              value,
              label: t(mk(`people.report.reason.${value}`)),
            }))}
            onValueChange={(value) => {
              if (isReason(value)) setReason(value);
            }}
          />
          <TextArea
            label={t("people.report.note")}
            hint={t("people.report.note.hint")}
            maxLength={500}
            value={note}
            onChange={(event) => {
              setNote(event.target.value);
            }}
          />
          {error !== undefined && <ErrorBanner message={error} />}
          <Button
            full
            tone="primary"
            label={t("people.report.submit")}
            disabled={sending}
            onPress={send}
          />
        </Column>
      )}
      {blocked && <Banner tone="ready" message={t("people.state.blocked")} />}
    </Sheet>
  );
}
